import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ImageIcon from '@mui/icons-material/Image';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { useTranslation } from 'react-i18next';
import ImageLibrarySelectionModal from '../../accounting/inventory/ImageLibrarySelectionModal';
import {
  BrandService,
  InventoryBrandDto,
} from '../../accounting/inventory/services/brandService';

interface BrandManagementProps {
  companyId: string | null;
}

const BrandManagement: React.FC<BrandManagementProps> = ({ companyId }) => {
  const { t } = useTranslation('salesSystem');
  const [brands, setBrands] = useState<InventoryBrandDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [selectedBrandId, setSelectedBrandId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const loadBrands = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const data = await BrandService.getAllBrands(companyId);
      setBrands(
        [...data].sort((a, b) => a.name.localeCompare(b.name, 'tr', { sensitivity: 'base' }))
      );
    } catch (error: any) {
      setFeedback({
        type: 'error',
        message: error?.message || t('ecommerceBrands.loadError'),
      });
    } finally {
      setLoading(false);
    }
  }, [companyId, t]);

  useEffect(() => {
    loadBrands();
  }, [loadBrands]);

  const filteredBrands = useMemo(() => {
    const q = search.trim().toLocaleLowerCase('tr');
    if (!q) return brands;
    return brands.filter((brand) => brand.name.toLocaleLowerCase('tr').includes(q));
  }, [brands, search]);

  const openLogoPicker = (brandId: string) => {
    setSelectedBrandId(brandId);
    setLibraryOpen(true);
  };

  const saveBrandImage = async (brand: InventoryBrandDto, imageUrl: string | null) => {
    if (!companyId) return;
    setSavingId(brand.id);
    try {
      const updated = await BrandService.updateBrand({
        companyId,
        id: brand.id,
        name: brand.name,
        description: brand.description,
        imageUrl: imageUrl || undefined,
        isActive: brand.isActive !== false,
      });
      setBrands((prev) =>
        prev.map((item) =>
          item.id === brand.id
            ? { ...item, imageUrl: updated.imageUrl ?? imageUrl ?? undefined }
            : item
        )
      );
      setFeedback({ type: 'success', message: t('ecommerceBrands.saveSuccess') });
    } catch (error: any) {
      setFeedback({
        type: 'error',
        message: error?.message || t('ecommerceBrands.saveError'),
      });
    } finally {
      setSavingId(null);
    }
  };

  const handleLibrarySave = async (urls: string[]) => {
    setLibraryOpen(false);
    const brand = brands.find((item) => item.id === selectedBrandId);
    const url = urls[0];
    setSelectedBrandId(null);
    if (!brand || !url) return;
    await saveBrandImage(brand, url);
  };

  const handleRemoveLogo = async (brand: InventoryBrandDto) => {
    await saveBrandImage(brand, null);
  };

  const handleVisibilityChange = async (brand: InventoryBrandDto, visible: boolean) => {
    if (!companyId) return;
    setSavingId(brand.id);
    setBrands((prev) =>
      prev.map((item) => (item.id === brand.id ? { ...item, isActive: visible } : item))
    );
    try {
      await BrandService.updateBrand({
        companyId,
        id: brand.id,
        name: brand.name,
        description: brand.description,
        imageUrl: brand.imageUrl || undefined,
        isActive: visible,
      });
      setFeedback({ type: 'success', message: t('ecommerceBrands.visibilitySaved') });
    } catch (error: any) {
      setBrands((prev) =>
        prev.map((item) => (item.id === brand.id ? { ...item, isActive: brand.isActive } : item))
      );
      setFeedback({
        type: 'error',
        message: error?.message || t('ecommerceBrands.saveError'),
      });
    } finally {
      setSavingId(null);
    }
  };

  if (!companyId) {
    return <Alert severity="info">{t('ecommerceBrands.companyRequired')}</Alert>;
  }

  return (
    <Box>
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <StorefrontIcon color="primary" />
            <Box>
              <Typography variant="h6">{t('ecommerceBrands.title')}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t('ecommerceBrands.help')}
              </Typography>
            </Box>
          </Box>
          <Tooltip title={t('ecommerceBrands.refresh')}>
            <span>
              <IconButton onClick={loadBrands} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        <TextField
          size="small"
          fullWidth
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('ecommerceBrands.searchPlaceholder')}
          sx={{ mb: 2, maxWidth: 360 }}
        />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : filteredBrands.length === 0 ? (
          <Alert severity="info">{t('ecommerceBrands.empty')}</Alert>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={96}>{t('ecommerceBrands.columns.logo')}</TableCell>
                  <TableCell>{t('ecommerceBrands.columns.name')}</TableCell>
                  <TableCell width={120}>{t('ecommerceBrands.columns.products')}</TableCell>
                  <TableCell width={160}>{t('ecommerceBrands.columns.visible')}</TableCell>
                  <TableCell width={220} align="right">
                    {t('ecommerceBrands.columns.actions')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredBrands.map((brand) => {
                  const busy = savingId === brand.id;
                  return (
                    <TableRow key={brand.id} hover sx={{ opacity: brand.isActive === false ? 0.55 : 1 }}>
                      <TableCell>
                        <Box
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            overflow: 'hidden',
                            bgcolor: 'grey.50',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {brand.imageUrl ? (
                            <Box
                              component="img"
                              src={brand.imageUrl}
                              alt={brand.name}
                              sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                            />
                          ) : (
                            <ImageIcon color="disabled" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {brand.name}
                        </Typography>
                      </TableCell>
                      <TableCell>{brand.itemCount ?? '—'}</TableCell>
                      <TableCell>
                        <Switch
                          size="small"
                          checked={brand.isActive !== false}
                          onChange={(e) => handleVisibilityChange(brand, e.target.checked)}
                          disabled={busy}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={busy ? <CircularProgress size={14} /> : <ImageIcon />}
                          onClick={() => openLogoPicker(brand.id)}
                          disabled={busy}
                          sx={{ mr: 1 }}
                        >
                          {brand.imageUrl
                            ? t('ecommerceBrands.actions.changeLogo')
                            : t('ecommerceBrands.actions.selectLogo')}
                        </Button>
                        {brand.imageUrl && (
                          <Tooltip title={t('ecommerceBrands.actions.removeLogo')}>
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveLogo(brand)}
                                disabled={busy}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <ImageLibrarySelectionModal
        open={libraryOpen}
        onClose={() => {
          setLibraryOpen(false);
          setSelectedBrandId(null);
        }}
        onSave={handleLibrarySave}
        companyId={companyId}
        productName={
          brands.find((item) => item.id === selectedBrandId)?.name || t('ecommerceBrands.libraryTitle')
        }
        currentImageUrls={
          brands.find((item) => item.id === selectedBrandId)?.imageUrl
            ? [brands.find((item) => item.id === selectedBrandId)!.imageUrl!]
            : []
        }
        productId={selectedBrandId}
        singleSelect
      />

      <Snackbar
        open={Boolean(feedback)}
        autoHideDuration={4000}
        onClose={() => setFeedback(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {feedback ? (
          <Alert severity={feedback.type} onClose={() => setFeedback(null)} sx={{ width: '100%' }}>
            {feedback.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default BrandManagement;
